/**
 * Hunts app keys (Settings > Hunts app keys): the platform owner saves the
 * Zoho API client id / secret once, so Zoho Mail and Zoho Calendar work
 * without touching Supabase secrets. Server side: the `app-settings` edge
 * function and `public.app_credentials` (service role only). Env vars on
 * the server still win when set (`source: 'env'`).
 */

import { supabase } from './supabase'
import { callFunction } from './sources'

export type KeySource = 'env' | 'app' | 'none'

export interface AppKeysStatus {
  zoho: {
    client_id_set: boolean
    client_secret_set: boolean
    source: KeySource
    client_id_hint: string | null
  }
  redirect_uri: string
}

/** True when the signed-in user is a Hunts platform owner. Never throws. */
export async function amPlatformOwner(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('am_platform_owner')
    return !error && data === true
  } catch {
    return false
  }
}

function asStatus(json: Record<string, unknown>): AppKeysStatus {
  const z = (json.zoho ?? {}) as Record<string, unknown>
  const source = z.source === 'env' || z.source === 'app' ? z.source : 'none'
  return {
    zoho: {
      client_id_set: z.client_id_set === true,
      client_secret_set: z.client_secret_set === true,
      source,
      client_id_hint: typeof z.client_id_hint === 'string' ? z.client_id_hint : null,
    },
    redirect_uri: typeof json.redirect_uri === 'string' ? json.redirect_uri : '',
  }
}

export async function fetchAppKeys(): Promise<AppKeysStatus> {
  return asStatus(await callFunction('app-settings', { action: 'status' }))
}

export async function saveZohoKeys(clientId: string, clientSecret: string): Promise<AppKeysStatus> {
  return asStatus(
    await callFunction('app-settings', {
      action: 'save_zoho',
      client_id: clientId.trim(),
      client_secret: clientSecret.trim(),
    }),
  )
}

export async function clearZohoKeys(): Promise<AppKeysStatus> {
  return asStatus(await callFunction('app-settings', { action: 'clear_zoho' }))
}
